<script setup lang="ts">
import { ref, watch } from 'vue'

import { Input } from '@/components/ui/input'
import { apiApiReportsFieldOptionsPath } from '@/routes'

const props = defineProps<{
  baseEntity: string
  field: string
  modelValue: string
}>()

const emit = defineEmits<{ 'update:modelValue': [string] }>()

const suggestions = ref<string[]>([])
const showSuggestions = ref(false)

let debounceTimer: ReturnType<typeof setTimeout>

async function fetchSuggestions(q: string) {
  if (!q) {
    suggestions.value = []
    return
  }

  const url = apiApiReportsFieldOptionsPath({ base_entity: props.baseEntity, field: props.field, q })
  const res = await fetch(url, { headers: { Accept: 'application/json' } })
  if (!res.ok) {
    suggestions.value = []
    return
  }

  const options: { value: string }[] = (await res.json()).options ?? []
  suggestions.value = options.map((o) => o.value).filter((v) => v !== props.modelValue)
}

function onInput(value: string | number) {
  const text = String(value)
  emit('update:modelValue', text)
  showSuggestions.value = true
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => fetchSuggestions(text), 250)
}

function choose(value: string) {
  emit('update:modelValue', value)
  showSuggestions.value = false
  suggestions.value = []
}

function onBlur() {
  setTimeout(() => (showSuggestions.value = false), 150)
}

watch(
  () => props.field,
  () => {
    suggestions.value = []
    showSuggestions.value = false
  },
)
</script>

<template>
  <div class="relative">
    <Input
      :model-value="modelValue"
      placeholder="Value"
      class="w-40"
      autocomplete="off"
      @update:model-value="onInput"
      @focus="showSuggestions = true"
      @blur="onBlur"
    />
    <ul
      v-if="showSuggestions && suggestions.length"
      class="bg-popover text-popover-foreground absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-md border p-1 shadow-md"
    >
      <li v-for="suggestion in suggestions" :key="suggestion">
        <button
          type="button"
          class="hover:bg-accent hover:text-accent-foreground w-full rounded-sm px-2 py-1.5 text-left text-sm"
          @mousedown.prevent="choose(suggestion)"
        >
          {{ suggestion }}
        </button>
      </li>
    </ul>
  </div>
</template>
